// Environment-driven web analytics (GA4 + Microsoft Clarity).
// Complete no-op when disabled or when IDs are empty. No console noise.
// When IDs are added via .env, tracking begins automatically — no code changes.

const GA_ID = (process.env.REACT_APP_GA4_MEASUREMENT_ID || "").trim();
const CLARITY_ID = (process.env.REACT_APP_CLARITY_PROJECT_ID || "").trim();
const ENABLED =
  String(process.env.REACT_APP_ANALYTICS_ENABLED || "").toLowerCase() === "true";

const gaOn = () => ENABLED && !!GA_ID;
const clarityOn = () => ENABLED && !!CLARITY_ID;

export const analyticsActive = () => gaOn() || clarityOn();

let initialized = false;

export function initAnalytics() {
  if (initialized || !analyticsActive()) return;
  initialized = true;
  if (gaOn()) loadGA();
  if (clarityOn()) loadClarity();
}

function loadGA() {
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  // We handle page views manually for SPA route changes.
  gtag("config", GA_ID, { send_page_view: false });
}

function loadClarity() {
  (function (c, l, a, r, i, t, y) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);
}

export function trackPageView(path) {
  if (!gaOn() || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

// Generic custom event. Safe no-op when analytics is off.
export function trackEvent(name, params = {}) {
  if (!analyticsActive()) return;
  try {
    if (gaOn() && window.gtag) window.gtag("event", name, params);
    if (clarityOn() && window.clarity) window.clarity("event", name);
  } catch (_) { /* noop */ }
}

// Named helpers for the product's key events. Easy to call from anywhere later.
export const AnalyticsEvents = {
  playerProfileViewed: (playerId) => trackEvent("player_profile_viewed", { player_id: playerId }),
  recruitingPortfolioViewed: (playerId) => trackEvent("recruiting_portfolio_viewed", { player_id: playerId }),
  membershipStarted: (tier) => trackEvent("membership_started", { tier }),
  membershipUpgraded: (tier) => trackEvent("membership_upgraded", { tier }),
  organizationCreated: (orgId) => trackEvent("organization_created", { org_id: orgId }),
  teamCreated: (teamId) => trackEvent("team_created", { team_id: teamId }),
  gameStarted: (gameId) => trackEvent("game_started", { game_id: gameId }),
  gameFinalized: (gameId) => trackEvent("game_finalized", { game_id: gameId }),
  recruiterRegistration: () => trackEvent("recruiter_registration", {}),
  campRegistration: (camp) => trackEvent("camp_registration", { camp }),
  searchPerformed: (query) => trackEvent("search_performed", { query }),
};
